import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef } from 'react'

import { useTenant } from '../tenant/TenantProvider'

/**
 * Makes a clinic the server just added to the organization appear in the
 * app: the organization answer and the clinic list (with that clinic chosen)
 * are reloaded once, when the success view that announced it closes.
 *
 * Either reload changes what the shell shows (a clinic list reload empties
 * the list while it runs; an organization's first clinic ends its empty
 * state), which would replace the page and throw away its success view. So
 * the reload waits for the view to close, by its own buttons (`leave`) or by
 * any navigation away. If the page was already left while the request was
 * still running, it happens as soon as the clinic exists.
 */
export function useRefreshClinicsOnLeave() {
  const { refreshClinics } = useTenant()
  const queryClient = useQueryClient()
  const latest = useRef({ refreshClinics, queryClient })
  const mounted = useRef(false)
  const pending = useRef<string | null>(null)
  useEffect(() => {
    latest.current = { refreshClinics, queryClient }
  }, [queryClient, refreshClinics])
  const publish = useCallback(() => {
    const tenantUid = pending.current
    if (!tenantUid) return
    pending.current = null
    void latest.current.queryClient.invalidateQueries({
      queryKey: ['organization-admin'],
    })
    latest.current.refreshClinics(tenantUid)
  }, [])
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      publish()
    }
  }, [publish])
  return {
    /** The server added this clinic (from the mutation's onSuccess). The
     * latest one becomes the selected clinic; the reload lists them all. */
    added: useCallback(
      (tenantUid: string) => {
        pending.current = tenantUid
        if (!mounted.current) publish()
      },
      [publish]
    ),
    /** The success view is closing through one of its own buttons. */
    leave: publish,
  }
}
